/**
 * Step processor — manages wizard step transitions, guards, and meta evolution.
 */

import { useReducer, useCallback } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import type { FormStep, ConditionalNextStep } from '../types/step';
import type { Condition } from '../types/conditions';
import { evaluateCondition } from '../evaluators/conditionEvaluator';
import type { ConditionContext } from '../evaluators/conditionEvaluator';
import { ActionRegistry } from '@packages/table-builder/registry';
import { notify } from '@packages/shared/utils';

function scrollToFirstError(form: UseFormReturn<any>): void {
  requestAnimationFrame(() => {
    const errorEl = document.querySelector('[aria-invalid="true"], .field-error, [data-error="true"]');
    if (errorEl) {
      errorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    const allErrors = form.formState.errors;
    const firstKey = findFirstErrorKey(allErrors);
    if (firstKey) {
      const el = document.querySelector(`[name="${firstKey}"]`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  });
}

function findFirstErrorKey(errors: Record<string, unknown>, prefix = ''): string | null {
  for (const key of Object.keys(errors)) {
    const val = errors[key];
    if (val == null) continue;
    const fullKey = prefix ? `${prefix}.${key}` : key;
    if (typeof val === 'object' && 'message' in (val as object)) {
      return fullKey;
    }
    if (typeof val === 'object' && !Array.isArray(val)) {
      const nested = findFirstErrorKey(val as Record<string, unknown>, fullKey);
      if (nested) return nested;
    }
    if (Array.isArray(val)) {
      for (let i = 0; i < val.length; i++) {
        if (val[i] && typeof val[i] === 'object') {
          const nested = findFirstErrorKey(val[i] as Record<string, unknown>, `${fullKey}.${i}`);
          if (nested) return nested;
        }
      }
    }
  }
  return null;
}

interface StepState {
  currentStepId: string;
  meta: Record<string, unknown>;
  isTransitioning: boolean;
  visitedSteps: Set<string>;
}

type StepAction =
  | { type: 'SET_STEP'; stepId: string }
  | { type: 'SET_TRANSITIONING'; value: boolean }
  | { type: 'UPDATE_META'; updates: Record<string, unknown> }
  | { type: 'MARK_VISITED'; stepId: string };

function stepReducer(state: StepState, action: StepAction): StepState {
  switch (action.type) {
    case 'SET_STEP':
      return { ...state, currentStepId: action.stepId, isTransitioning: false };
    case 'SET_TRANSITIONING':
      return { ...state, isTransitioning: action.value };
    case 'UPDATE_META':
      return { ...state, meta: { ...state.meta, ...action.updates } };
    case 'MARK_VISITED': {
      const visited = new Set(state.visitedSteps);
      visited.add(action.stepId);
      return { ...state, visitedSteps: visited };
    }
    default: {
      const _exhaustive: never = action;
      return state;
    }
  }
}

interface UseStepProcessorParams {
  steps: FormStep[];
  form: UseFormReturn<Record<string, unknown>>;
  initialMeta: Record<string, unknown>;
  getOptionProp: (field: string, prop: string) => unknown;
  enabled?: boolean;
  onStepSubmit?: (
    values: Record<string, unknown>,
    stepId: string,
  ) => Promise<Record<string, unknown> | void>;
  onMetaUpdate?: (meta: Record<string, unknown>) => void;
  onSuccess?: () => void;
}

function extractMetaUpdates(
  updateMeta: Record<string, string>,
  result: Record<string, unknown>,
): Record<string, unknown> {
  const metaUpdates: Record<string, unknown> = {};
  for (const [metaKey, resultPath] of Object.entries(updateMeta)) {
    const path = resultPath.replace(/^\{result\./, '').replace(/\}$/, '');
    metaUpdates[metaKey] = result[path];
  }
  return metaUpdates;
}

function sanitizeStepPayload(
  allValues: Record<string, unknown>,
  fieldNames: string[],
): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const name of fieldNames) {
    const parts = name.split('.');
    let src: unknown = allValues;
    for (const part of parts) {
      if (src == null || typeof src !== 'object') { src = undefined; break; }
      src = (src as Record<string, unknown>)[part];
    }
    if (src !== undefined) {
      let target = result;
      for (let i = 0; i < parts.length - 1; i++) {
        if (target[parts[i]] == null || typeof target[parts[i]] !== 'object') {
          target[parts[i]] = {};
        }
        target = target[parts[i]] as Record<string, unknown>;
      }
      target[parts[parts.length - 1]] = src;
    }
  }
  return result;
}

export interface StepProcessorReturn {
  currentStep: FormStep;
  currentStepIndex: number;
  visibleSteps: FormStep[];
  meta: Record<string, unknown>;
  isTransitioning: boolean;
  canGoBack: boolean;
  canGoNext: boolean;
  goNext: () => Promise<void>;
  goBack: () => void;
  goToStep: (stepId: string) => void;
  executeSecondaryAction: () => Promise<void>;
}

const EMPTY_STEPS: FormStep[] = [];
const EMPTY_VISITED = new Set<string>();

export function useStepProcessor({
  steps: rawSteps,
  form,
  initialMeta,
  getOptionProp,
  enabled = true,
  onStepSubmit,
  onMetaUpdate,
  onSuccess,
}: UseStepProcessorParams): StepProcessorReturn {
  const steps = enabled ? rawSteps : EMPTY_STEPS;
  const [state, dispatch] = useReducer(stepReducer, {
    currentStepId: steps[0]?.id ?? '',
    meta: initialMeta,
    isTransitioning: false,
    visitedSteps: steps[0] ? new Set([steps[0].id]) : EMPTY_VISITED,
  });

  const buildContext = useCallback((): ConditionContext => ({
    fieldValue: undefined,
    values: form.getValues(),
    meta: state.meta,
    getOptionProp,
  }), [form, state.meta, getOptionProp]);

  const visibleSteps = steps.filter((step) => {
    if (!step.guard) return true;
    return evaluateCondition(step.guard, buildContext());
  });

  const currentStep = steps.find((s) => s.id === state.currentStepId) ?? steps[0];
  const currentVisibleIndex = visibleSteps.findIndex((s) => s.id === state.currentStepId);

  const canGoBack = currentVisibleIndex > 0;
  const canGoNext = currentVisibleIndex < visibleSteps.length - 1;

  const goNext = useCallback(async () => {
    if (!currentStep?.onNext && !canGoNext) return;

    const stepFieldNames = collectStepFieldNames(currentStep, form);
    if (stepFieldNames.length > 0) {
      stepFieldNames.forEach(name => {
        form.setValue(name, form.getValues(name), { shouldTouch: true });
      });
      const isValid = await form.trigger(stepFieldNames as any);
      if (!isValid) {
        scrollToFirstError(form);
        return;
      }
    }

    dispatch({ type: 'SET_TRANSITIONING', value: true });

    try {
      let localMeta = state.meta;

      if (currentStep.onNext?.actionId) {
        const result = await ActionRegistry.executeData(currentStep.onNext.actionId, form.getValues() as Record<string, string>);

        if (currentStep.onNext.updateMeta && result && typeof result === 'object') {
          const metaUpdates = extractMetaUpdates(
            currentStep.onNext.updateMeta,
            result as Record<string, unknown>,
          );
          localMeta = { ...localMeta, ...metaUpdates };
          dispatch({ type: 'UPDATE_META', updates: metaUpdates });
          onMetaUpdate?.(metaUpdates);
        }
      }

      let nextStepId: string;
      if (currentStep.onNext?.nextStep) {
        if (typeof currentStep.onNext.nextStep === 'string') {
          nextStepId = currentStep.onNext.nextStep;
        } else {
          const conditional = currentStep.onNext.nextStep as ConditionalNextStep;
          const ctx: ConditionContext = {
            fieldValue: undefined,
            values: form.getValues(),
            meta: localMeta,
            getOptionProp,
          };
          nextStepId = evaluateCondition(conditional.condition, ctx)
            ? conditional.then
            : conditional.else;
        }
      } else {
        const nextVisible = visibleSteps[currentVisibleIndex + 1];
        nextStepId = nextVisible?.id ?? state.currentStepId;
      }

      if (currentStep.onNext?.submit && onStepSubmit) {
        const stepValues = sanitizeStepPayload(form.getValues(), stepFieldNames);
        const submitResult = await onStepSubmit(stepValues, currentStep.id);

        if (
          currentStep.onNext.updateMeta &&
          submitResult &&
          typeof submitResult === 'object'
        ) {
          const metaUpdates = extractMetaUpdates(
            currentStep.onNext.updateMeta,
            submitResult as Record<string, unknown>,
          );
          localMeta = { ...localMeta, ...metaUpdates };
          dispatch({ type: 'UPDATE_META', updates: metaUpdates });
          onMetaUpdate?.(metaUpdates);
        }

        const stepResponseUpdate = { [`stepResponses.${currentStep.id}`]: stepValues };
        localMeta = { ...localMeta, ...stepResponseUpdate };
        dispatch({ type: 'UPDATE_META', updates: stepResponseUpdate });
        onMetaUpdate?.(stepResponseUpdate);

        if (currentStep.onNext.exitAfter) {
          dispatch({ type: 'SET_TRANSITIONING', value: false });
          onSuccess?.();
          return;
        }
      }

      dispatch({ type: 'MARK_VISITED', stepId: nextStepId });
      dispatch({ type: 'SET_STEP', stepId: nextStepId });
    } catch (err: unknown) {
      dispatch({ type: 'SET_TRANSITIONING', value: false });

      const error = err as { message?: string; errors?: Record<string, string> } | undefined;

      if (error?.errors && typeof error.errors === 'object') {
        for (const [field, message] of Object.entries(error.errors)) {
          form.setError(field, { type: 'server', message: message as string });
        }
      }

      const errorMsg = error?.message ?? 'An error occurred. Please try again.';
      notify.error(errorMsg);
    }
  }, [currentStep, canGoNext, form, state.meta, getOptionProp, visibleSteps, currentVisibleIndex, state.currentStepId, onStepSubmit, onSuccess]);

  const goBack = useCallback(() => {
    if (!canGoBack) return;
    const prevStep = visibleSteps[currentVisibleIndex - 1];
    if (prevStep) {
      dispatch({ type: 'SET_STEP', stepId: prevStep.id });
    }
  }, [canGoBack, visibleSteps, currentVisibleIndex]);

  const goToStep = useCallback((stepId: string) => {
    if (state.visitedSteps.has(stepId)) {
      dispatch({ type: 'SET_STEP', stepId });
    }
  }, [state.visitedSteps]);

  const executeSecondaryAction = useCallback(async () => {
    const secondary = currentStep.onNext?.secondaryAction;
    if (!secondary) return;

    const stepFieldNames = collectStepFieldNames(currentStep, form);
    if (stepFieldNames.length > 0) {
      stepFieldNames.forEach(name => {
        form.setValue(name, form.getValues(name), { shouldTouch: true });
      });
      const isValid = await form.trigger(stepFieldNames as any);
      if (!isValid) {
        scrollToFirstError(form);
        return;
      }
    }

    dispatch({ type: 'SET_TRANSITIONING', value: true });
    try {
      if (secondary.submit && onStepSubmit) {
        const stepValues = sanitizeStepPayload(form.getValues(), stepFieldNames);
        await onStepSubmit(stepValues, currentStep.id);
      }

      dispatch({ type: 'SET_TRANSITIONING', value: false });

      if (secondary.exitAfter) {
        onSuccess?.();
      }
    } catch (err: unknown) {
      dispatch({ type: 'SET_TRANSITIONING', value: false });
      const error = err as { message?: string; errors?: Record<string, string> } | undefined;
      if (error?.errors && typeof error.errors === 'object') {
        for (const [field, message] of Object.entries(error.errors)) {
          form.setError(field, { type: 'server', message: message as string });
        }
      }
      const errorMsg = error?.message ?? 'An error occurred. Please try again.';
      notify.error(errorMsg);
    }
  }, [currentStep, form, onStepSubmit, onSuccess]);

  return {
    currentStep,
    currentStepIndex: currentVisibleIndex,
    visibleSteps,
    meta: state.meta,
    isTransitioning: state.isTransitioning,
    canGoBack,
    canGoNext,
    goNext,
    goBack,
    goToStep,
    executeSecondaryAction,
  };
}

function collectStepFieldNames(
  step: FormStep,
  form: UseFormReturn<Record<string, unknown>>,
): string[] {
  const names: string[] = [];
  for (const slot of step.blocks) {
    collectFieldNamesFromLayout(slot.use.layout, names, form);
  }
  return names;
}

function collectFieldNamesFromLayout(
  nodes: import('../types/layout').FormLayoutNode[],
  names: string[],
  form: UseFormReturn<Record<string, unknown>>,
): void {
  for (const node of nodes) {
    switch (node.type) {
      case 'fields':
        for (const field of node.fields) {
          if ('name' in field && (field as { name: string; hidden?: boolean }).name && !(field as { hidden?: boolean }).hidden) {
            names.push((field as { name: string }).name);
          }
        }
        break;
      case 'section':
      case 'grid':
      case 'stack':
      case 'surface':
        collectFieldNamesFromLayout(node.children, names, form);
        break;
      case 'tabs':
        for (const tab of node.tabs) {
          collectFieldNamesFromLayout(tab.children, names, form);
        }
        break;
      case 'conditional':
        collectFieldNamesFromLayout(node.children, names, form);
        if (node.else) collectFieldNamesFromLayout(node.else, names, form);
        break;
      case 'each': {
        const arrayValue = form.getValues(node.name) as unknown[] | undefined;
        if (!arrayValue?.length) {
          names.push(node.name);
          break;
        }
        for (let i = 0; i < arrayValue.length; i++) {
          if (node.primitiveItem) {
            names.push(`${node.name}.${i}`);
          } else {
            for (const field of node.itemFields) {
              if (field.name) names.push(`${node.name}.${i}.${field.name}`);
            }
          }
        }
        break;
      }
      case 'udf': {
        const prefix = node.fieldPrefix ?? 'userDefinedFields';
        const udfArray = form.getValues(prefix) as Array<{ name?: string; value?: unknown }> | undefined;
        if (udfArray?.length) {
          for (let i = 0; i < udfArray.length; i++) {
            names.push(`${prefix}.${i}.value`);
          }
        }
        break;
      }
      case 'column-group':
        for (const group of node.groups) {
          for (const field of group.fields) {
            if ('name' in field && (field as { name: string; hidden?: boolean }).name && !(field as { hidden?: boolean }).hidden) {
              names.push((field as { name: string }).name);
            }
          }
        }
        break;
      default:
        break;
    }
  }
}
